<template>
  <div class="question-manager">
    <router-link :to="{ name: 'AdminDashboard' }">&larr; Kembali ke Dashboard</router-link>
    <h1>Manajemen Soal: {{ testName }}</h1>

    <!-- Daftar Pertanyaan yang Ada -->
    <div class="card">
      <h2>Daftar Soal</h2>
      <div v-if="isLoading">Memuat soal...</div>
      <div v-else-if="questions.length > 0" class="question-list">
        <div v-for="q in questions" :key="q.id" class="question-item">
          <div class="question-text">
            <strong>Q{{ q.order }}:</strong> {{ q.text }}
          </div>
          <div class="question-meta">
            <span class="badge" :class="q.question_type">{{ formatType(q.question_type) }}</span>
            <span v-if="q.is_example" class="badge example">Contoh Soal</span>
          </div>
          <div class="question-actions">
            <button class="action-btn edit-btn" @click="startEdit(q)">Edit</button>
            <button class="action-btn delete-btn" @click="deleteQuestion(q.id)">Hapus</button>
          </div>
        </div>
      </div>
      <p v-else>Belum ada soal untuk tes ini.</p>
    </div>

    <div class="card">
      <h2>{{ isEditing ? 'Edit Soal' : 'Tambah Soal Baru' }}</h2>
      <form @submit.prevent="handleSubmit"> <!-- Gunakan handleSubmit -->
        <div class="form-group">
          <label for="q-type">Tipe Soal</label>
          <select id="q-type" v-model="newQuestion.question_type">
            <option value="multiple_choice">Pilihan Ganda (Satu Jawaban)</option>
            <option value="multiple_answer">Pilihan Ganda (Banyak Jawaban)</option> <!-- OPSI BARU -->
            <option value="disc">DISC (Most/Least)</option>
            <option value="essay">Essay</option>
          </select>
        </div>
        
        <!-- Ganti <textarea v-model="newQuestion.text"> dengan ini -->
        <div class="form-group">
          <label>Teks Pertanyaan</label>
          <div v-if="editor" class="editor-toolbar">
            <button @click="editor.chain().focus().toggleBold().run()" :class="{ 'is-active': editor.isActive('bold') }">Bold</button>
            <button @click="editor.chain().focus().toggleItalic().run()" :class="{ 'is-active': editor.isActive('italic') }">Italic</button>
            <button @click="editor.chain().focus().toggleBulletList().run()" :class="{ 'is-active': editor.isActive('bulletList') }">Bullet</button>
            <button @click="editor.chain().focus().toggleOrderedList().run()" :class="{ 'is-active': editor.isActive('orderedList') }">Number</button>
          </div>
          <EditorContent :editor="editor" class="editor-content" />
        </div>

        <div class="form-group-inline">
          <div class="form-group">
            <label for="q-order">Urutan</label>
            <input id="q-order" v-model.number="newQuestion.order" type="number" required />
          </div>
          <div class="form-group">
            <label for="q-image-upload">Upload Gambar (Opsional)</label>
            <input id="q-image-upload" type="file" @change="handleImageUpload" accept="image/png, image/jpeg, image/gif" />
            <!-- Tampilkan preview gambar dan URL yang dihasilkan -->
            <div v-if="newQuestion.image_url" class="image-preview">
              <img :src="`https://elevaretes.com${newQuestion.image_url}`" alt="Preview Gambar">
              <p>URL: <code>{{ newQuestion.image_url }}</code></p>
            </div>
            <small v-if="isUploading">Mengupload gambar...</small>
          </div>
        </div>

        <div class="form-group-checkbox">
          <input id="q-example" v-model="newQuestion.is_example" type="checkbox" />
          <label for="q-example">Tandai sebagai contoh soal</label>
        </div>
        
        <template v-if="newQuestion.question_type === 'multiple_choice' || newQuestion.question_type === 'multiple_answer' || newQuestion.question_type === 'disc' || newQuestion.question_type === 'essay'">
          <hr>
          <h3>{{ newQuestion.question_type === 'essay' ? 'Kata Kunci Penilaian (Essay)' : 'Pilihan Jawaban' }}</h3>
          <div v-for="(option, index) in newQuestion.options" :key="index" class="option-form-wrapper">
            <div class="option-form">
              <input v-model="option.text" :placeholder="newQuestion.question_type === 'essay' ? 'Kata Kunci (Contoh: Bunga)' : 'Teks Jawaban'" class="option-text"/>
              <input v-if="newQuestion.question_type !== 'disc'" v-model.number="option.score" type="number" placeholder="Skor" class="option-score" />
              <input v-if="newQuestion.question_type === 'disc'" v-model="option.category" type="text" placeholder="Kategori (D/I/S/C/*)" class="option-category" />
              
              <!-- Input File Baru untuk Setiap Opsi -->
              <div class="option-image-upload" v-if="newQuestion.question_type !== 'essay'">
                <label :for="'option-file-' + index" class="file-label">
                  {{ option.image_url ? 'Ganti Gambar' : 'Upload Gambar' }}
                </label>
                <input 
                  type="file" 
                  :id="'option-file-' + index"
                  @change="handleOptionImageUpload($event, index)"
                  accept="image/*"
                  style="display: none;"
                />
              </div>
              
              <button type="button" @click="removeOption(index)" class="remove-btn">Hapus</button>
            </div>
            <!-- Preview Gambar untuk Opsi -->
            <div v-if="option.image_url" class="option-image-preview">
              <img :src="`https://elevaretes.com${option.image_url}`" />
              <code>{{ option.image_url }}</code>
            </div>
          </div>
          <button type="button" @click="addOption" class="add-option-btn">{{ newQuestion.question_type === 'essay' ? 'Tambah Kata Kunci' : 'Tambah Pilihan Jawaban' }}</button>
        </template>
        
        <hr>
        <button type="submit" :disabled="isSubmitting" class="submit-btn">Simpan Pertanyaan</button>
        <button v-if="isEditing" type="button" @click="cancelEdit" class="cancel-btn">Batal</button>
      </form>
    </div>
    <p v-if="error" class="error">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted,  watch, onBeforeUnmount } from 'vue'; // <-- Tambahkan 'onBeforeUnmount'
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';

import { useRoute } from 'vue-router';
import apiClient from '@/services/api.js';

const route = useRoute();
const testId = route.params.id;
const testName = ref(route.query.name || 'Tes');

const questions = ref([]);
const isLoading = ref(true);
const isSubmitting = ref(false);
const isUploading = ref(false);
const error = ref('');


// --- State Baru ---
const isEditing = ref(false);
const currentQuestionId = ref(null);

// Fungsi untuk membuat objek pertanyaan baru yang bersih
const createDefaultQuestion = () => ({
  text: '',
  order: questions.value.length + 1,
  is_example: false,
  question_type: 'multiple_choice',
  image_url: '',
  options: [
    { text: '', score: 0, category: '' },
    { text: '', score: 0, category: '' },
  ],
});

const newQuestion = ref({
  text: '',
  order: 1,
  is_example: false,
  question_type: 'multiple_choice', // <-- Tambahkan ini
  image_url: '',
  options: [
    { text: '', score: 0, category: '' },
    { text: '', score: 0, category: '' },
    { text: '', score: 0, image_url: null, category: '' }, // <-- Tambahkan image_url
    { text: '', score: 0, image_url: null, category: '' },
  ],
});

const fetchQuestions = async () => {
  isLoading.value = true;
  try {
    const response = await apiClient.get(`/admin/tests/${testId}/questions`);
    questions.value = response.data;
  } catch (err) {
    error.value = "Gagal memuat soal.";
    console.error(err);
  } finally {
    isLoading.value = false;
  }
};

const addOption = () => {
  newQuestion.value.options.push({ text: '', score: 0 });
  newQuestion.value.options.push({ text: '', score: 0, image_url: null }); // <-- Tambahkan image_url
};

const removeOption = (index) => {
  newQuestion.value.options.splice(index, 1);
};

const addQuestion = async () => {
  isSubmitting.value = true;
  error.value = '';
  try {
    const payload = { ...newQuestion.value };
    // Dihapus: logika menghapus options jika essay, karena sekarang essay butuh opsi untuk kata kunci penilaian
    
    await apiClient.post(`/admin/tests/${testId}/questions`, payload);
    
    // Reset form menggunakan fungsi
    newQuestion.value = createDefaultQuestion();
    
    await fetchQuestions();
  } catch (err) {
    error.value = "Gagal menyimpan soal.";
    console.error(err);
  } finally {
    isSubmitting.value = false;
  }
};

const handleImageUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  isUploading.value = true;
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await apiClient.post('/admin/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    // Set URL gambar di form setelah berhasil di-upload
    newQuestion.value.image_url = response.data.file_url;
  } catch (err) {
    error.value = "Gagal mengupload gambar.";
    console.error(err);
  } finally {
    isUploading.value = false;
  }
};


// --- DEKLARASIKAN FUNGSI DENGAN 'const' ---
const formatType = (type) => {
  if (type === 'multiple_choice') return 'Pilihan Ganda';
  if (type === 'multiple_answer') return 'Banyak Jawaban';
  if (type === 'disc') return 'DISC';
  if (type === 'essay') return 'Essay';
  return type;
};

const deleteQuestion = async (questionId) => {
  if (!window.confirm("Apakah Anda yakin ingin menghapus soal ini?")) {
    return;
  }
  try {
    await apiClient.delete(`/admin/questions/${questionId}`);
    await fetchQuestions();
  } catch (err) {
    error.value = "Gagal menghapus soal.";
    console.error(err);
  }
};

const handleOptionImageUpload = async (event, index) => {
  const file = event.target.files[0];
  if (!file) return;

  const formData = new FormData();
  formData.append('file', file);
  
  // Tampilkan state loading jika perlu
  try {
    const response = await apiClient.post('/admin/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    // Update URL gambar untuk opsi yang spesifik
    newQuestion.value.options[index].image_url = response.data.file_url;
  } catch (err) {
    alert("Gagal mengupload gambar pilihan.");
    console.error(err);
  }
};

// --- Fungsi Baru & Modifikasi ---
const resetForm = () => {
  isEditing.value = false;
  currentQuestionId.value = null;
  newQuestion.value = {
    text: '',
    order: questions.value.length + 1, // Urutan berikutnya
    is_example: false,
    question_type: 'multiple_choice',
    image_url: '',
    options: [{ text: '', score: 0, category: '' }, { text: '', score: 0, category: '' }],
  };
};

// src/views/admin/QuestionManager.vue
const startEdit = (question) => {
  isEditing.value = true;
  currentQuestionId.value = question.id;

  // Sekarang 'question' dari API sudah memiliki semua field yang dibutuhkan
  newQuestion.value = {
    text: question.text,
    order: question.order,
    is_example: question.is_example,
    question_type: question.question_type,
    image_url: question.image_url || '',
    // Salin options, pastikan semua field (id, text, score, is_correct) ada
    options: question.options.map(opt => ({ ...opt })),
  };
  
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const cancelEdit = () => {
  resetForm();
};

const handleSubmit = async () => {
  if (isEditing.value) {
    await updateQuestion();
  } else {
    await addQuestion();
  }
};

const updateQuestion = async () => {
  isSubmitting.value = true;
  error.value = '';
  try {
    await apiClient.put(`/admin/questions/${currentQuestionId.value}`, newQuestion.value);
    resetForm();
    await fetchQuestions();
  } catch (err) {
    error.value = "Gagal mengupdate soal.";
    console.error(err);
  } finally {
    isSubmitting.value = false;
  }
};

const editor = useEditor({
  content: '<p>Tulis teks pertanyaan di sini...</p>',
  extensions: [
    StarterKit,
  ],
  onUpdate: ({ editor }) => {
    // Sinkronkan konten editor dengan state newQuestion
    newQuestion.value.text = editor.getHTML();
  },
});

// Jika masuk mode edit, update konten editor
watch(() => newQuestion.value.text, (newText) => {
    if (editor.value && editor.value.getHTML() !== newText) {
        editor.value.commands.setContent(newText);
    }
});

onBeforeUnmount(() => {
    editor.value.destroy();
});

onMounted(fetchQuestions);
</script>

<style scoped>
.question-manager {
  max-width: 960px;
  margin: 0 auto;
  padding: 30px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.router-link {
  display: inline-block;
  margin-bottom: 24px;
  color: #3498db;
  text-decoration: none;
  font-size: 0.95em;
  transition: color 0.2s ease;
}
.router-link:hover {
  color: #2980b9;
  text-decoration: underline;
}

h1 {
  margin: 0 0 24px 0;
  color: #2c3e50;
  font-size: 1.8em;
  font-weight: 600;
  line-height: 1.3;
}

.card {
  margin-top: 24px;
  padding: 28px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  border: 1px solid #eee;
}

.card h2 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 1.4em;
  font-weight: 600;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 12px;
}

/* --- Loading & Error --- */
.loading,
.error {
  text-align: center;
  color: #7f8c8d;
  font-size: 1.1em;
  margin: 40px 0;
}

.error {
  color: #e74c3c;
  font-weight: 500;
}

/* --- Daftar Soal --- */
.question-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.question-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border: 1px solid #eaeaea;
  border-radius: 10px;
  background-color: #fafafa;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.question-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.question-text {
  flex-grow: 1;
  font-size: 1em;
  color: #333;
}
.question-text strong {
  color: #34495e;
  margin-right: 8px;
}

.question-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: 600;
  color: white;
  text-transform: capitalize;
}

.badge.multiple_choice {
  background-color: #3498db;
}

.badge.multiple_answer {
  background-color: #e74c3c;
}

.badge.disc {
  background-color: #9b59b6;
}

.badge.essay {
  background-color: #1abc9c;
}

.badge.example {
  background-color: #f39c12;
  color: #2c3e50;
}

.question-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 0.9em;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.edit-btn {
  background-color: #f39c12;
  color: white;
}
.edit-btn:hover {
  background-color: #e67e22;
}

.delete-btn {
  background-color: #e74c3c;
  color: white;
}
.delete-btn:hover {
  background-color: #c0392b;
}

/* --- Form Tambah/Edit Soal --- */
form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: #333;
  font-size: 0.95em;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1em;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #1abc9c;
  box-shadow: 0 0 0 2px rgba(26, 188, 156, 0.2);
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.form-group-inline {
  display: flex;
  gap: 20px;
}

.form-group-inline .form-group {
  flex: 1;
}

.form-group-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.form-group-checkbox input {
  width: 18px;
  height: 18px;
}

.form-group-checkbox label {
  margin: 0;
  font-weight: 500;
  color: #555;
  font-size: 0.95em;
}

/* --- Pilihan Jawaban --- */
.option-form {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}

.option-form .option-text {
  flex-grow: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.option-form .option-score {
  width: 70px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.remove-btn {
  padding: 10px 14px;
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9em;
  font-weight: 500;
}

.remove-btn:hover {
  background-color: #c0392b;
}

.add-option-btn {
  align-self: flex-start;
  padding: 10px 16px;
  background-color: #1abc9c;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95em;
  font-weight: 500;
  margin-bottom: 20px;
}
.add-option-btn:hover {
  background-color: #16a085;
}

/* --- Tombol Submit & Batal --- */
.submit-btn,
.cancel-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.submit-btn {
  background-color: #28a745;
  color: white;
  align-self: flex-end;
}
.submit-btn:hover:not(:disabled) {
  background-color: #218838;
}
.submit-btn:disabled {
  background-color: #bdc3c7;
  cursor: not-allowed;
}

.cancel-btn {
  background-color: #6c757d;
  color: white;
  align-self: flex-end;
  margin-left: 12px;
}
.cancel-btn:hover {
  background-color: #5a6268;
}

/* --- Preview Gambar --- */
.image-preview {
  margin-top: 15px;
  padding: 15px;
  border: 1px dashed #ccc;
  border-radius: 8px;
  background-color: #f9f9f9;
}

.image-preview img {
  max-width: 200px;
  max-height: 150px;
  border-radius: 6px;
  object-fit: contain;
  margin-bottom: 8px;
}

.image-preview p {
  margin: 0;
  font-size: 0.8em;
  color: #7f8c8d;
  word-break: break-all;
}

.small {
  font-size: 0.85em;
  color: #95a5a6;
  margin-top: 5px;
}

/* --- Responsif --- */
@media (max-width: 768px) {
  .question-manager {
    padding: 20px;
  }

  .card {
    padding: 20px;
  }

  .card h2 {
    font-size: 1.3em;
  }

  .question-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .question-meta,
  .question-actions {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .form-group-inline {
    flex-direction: column;
    gap: 12px;
  }

  .submit-btn,
  .cancel-btn {
    width: 100%;
    margin-top: 10px;
  }
  .cancel-btn {
    margin-left: 0;
  }
}

.option-form-wrapper { border: 1px solid #f0f0f0; padding: 10px; border-radius: 5px; margin-bottom: 10px; }
.file-label { padding: 6px 10px; background-color: #3498db; color: white; border-radius: 4px; cursor: pointer; white-space: nowrap; }
.option-image-preview { margin-top: 10px; }
.option-image-preview img { max-height: 50px; border: 1px solid #ddd; border-radius: 4px; }

.editor-toolbar {
  display: flex;
  gap: 5px;
  margin-bottom: 5px;
  border: 1px solid #ccc;
  border-bottom: none;
  padding: 5px;
  border-radius: 4px 4px 0 0;
}
.editor-toolbar button {
  padding: 4px 8px;
  border: 1px solid transparent;
  background: none;
  cursor: pointer;
}
.editor-toolbar button.is-active {
  background-color: #eee;
  border-radius: 4px;
}

/* Style untuk area konten editor */
.editor-content {
  border: 1px solid #ccc;
  padding: 10px;
  min-height: 150px; /* Beri tinggi minimum */
  border-radius: 0 0 4px 4px;
}
.editor-content:focus-within {
  border-color: #1abc9c;
  box-shadow: 0 0 0 2px rgba(26, 188, 156, 0.2);
}
</style>